/**
 * schema.org Organization node for the marketplace.
 * Mirrors the fields Page.js has always emitted, and adds an optional
 * `description` so search engines get a fuller picture of the entity.
 * `address` is only included when a streetAddress is present (Google ignores
 * Organization PostalAddress otherwise, and an all-null address is noise).
 */
export const organizationSchema = ({
  marketplaceRootURL,
  marketplaceName,
  sameAs,
  logo,
  description,
  address,
}) => {
  const addressMaybe = address?.streetAddress ? { address } : {};
  const descriptionMaybe = description ? { description } : {};
  return {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    '@id': `${marketplaceRootURL}#organization`,
    url: marketplaceRootURL,
    name: marketplaceName,
    sameAs,
    logo,
    ...descriptionMaybe,
    ...addressMaybe,
  };
};

/**
 * schema.org Organization node for the supplier behind a listing's Offer.
 * `seller` is a valid Offer property; `provider` is not valid on Product, which
 * is why this hangs off the Offer rather than the Product.
 */
export const sellerSchema = displayName =>
  displayName ? { '@type': 'Organization', name: displayName } : null;

/**
 * The cantons a supplier covers, as schema.org AdministrativeArea nodes.
 * Listings carry no geolocation or street address, so the Region field is the
 * only location signal available - and it is the more useful one anyway, since
 * it states where the supplier works rather than where they happen to be based.
 *
 * Region stores ASCII option keys ("Zuerich", "St-Gallen") that are not place
 * names, so the display label from the hosted field config is what gets
 * emitted. Reading the config rather than hardcoding a map keeps this correct
 * when cantons are edited in Console.
 */
export const areaServedSchema = (regions, listingFields = []) => {
  if (!Array.isArray(regions) || regions.length === 0) {
    return null;
  }

  const enumOptions = listingFields.find(field => field.key === 'Region')?.enumOptions || [];

  return regions.map(region => ({
    '@type': 'AdministrativeArea',
    name: enumOptions.find(option => option.option === region)?.label || region,
  }));
};

/**
 * schema.org WebSite node.
 * Adds a SearchAction (the "sitelinks search box") pointing at the marketplace
 * search page (/s?keywords=...), and links the site to its publishing
 * Organization via @id so the two nodes are understood as one entity.
 */
export const webSiteSchema = ({ marketplaceRootURL, name, description }) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    '@id': `${marketplaceRootURL}#website`,
    url: marketplaceRootURL,
    name,
    description,
    publisher: { '@id': `${marketplaceRootURL}#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${marketplaceRootURL}/s?keywords={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

/**
 * schema.org BreadcrumbList from an ordered list of `{ name, url }` crumbs.
 * Returns null when there is nothing to show, so callers can spread/filter it
 * out safely (e.g. `[pageSchema, breadcrumbListSchema(crumbs)].filter(Boolean)`).
 */
export const breadcrumbListSchema = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  return {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

const ensureOpenGraphLocale = locale => {
  switch (locale) {
    case 'en':
      return 'en_US';
    default:
      return locale;
  }
};

/**
 * These will be used with Helmet <meta {...openGraphMetaProps} />
 */
export const openGraphMetaProps = data => {
  const {
    openGraphType,
    socialSharingTitle,
    socialSharingDescription,
    published,
    updated,
    url,
    locale,
    facebookImages,
    facebookAppId,
    marketplaceName,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && openGraphType && url && facebookImages)) {
    /* eslint-disable no-console */
    if (console && console.warn) {
      console.warn(
        `Can't create Open Graph meta tags:
        socialSharingTitle, socialSharingDescription, openGraphType, url, and facebookImages are needed.`
      );
    }
    /* eslint-enable no-console */
    return [];
  }

  const openGraphMeta = [
    { property: 'og:description', content: socialSharingDescription },
    { property: 'og:title', content: socialSharingTitle },
    { property: 'og:type', content: openGraphType },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: ensureOpenGraphLocale(locale) },
  ];

  if (facebookImages && facebookImages.length > 0) {
    facebookImages.forEach(i => {
      openGraphMeta.push({
        property: 'og:image',
        content: i.url,
      });

      if (i.width && i.height) {
        openGraphMeta.push({ property: 'og:image:width', content: i.width });
        openGraphMeta.push({ property: 'og:image:height', content: i.height });
      }
    });
  }

  if (marketplaceName) {
    openGraphMeta.push({ property: 'og:site_name', content: marketplaceName });
  }

  if (facebookAppId) {
    openGraphMeta.push({ property: 'fb:app_id', content: facebookAppId });
  }

  if (published) {
    openGraphMeta.push({ property: 'article:published_time', content: published });
  }

  if (updated) {
    openGraphMeta.push({ property: 'article:modified_time', content: updated });
  }

  return openGraphMeta;
};

/**
 * These will be used with Helmet <meta {...twitterMetaProps} />
 */
export const twitterMetaProps = data => {
  const {
    socialSharingTitle,
    socialSharingDescription,
    twitterHandle,
    twitterImages,
    url,
    marketplaceRootURL,
    siteTwitterHandle,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && url)) {
    /* eslint-disable no-console */
    if (console && console.warn) {
      console.warn(
        `Can't create twitter card meta tags:
        socialSharingTitle, socialSharingDescription, and url are needed.`
      );
    }
    /* eslint-enable no-console */
    return [];
  }

  const twitterMeta = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialSharingTitle },
    { name: 'twitter:description', content: socialSharingDescription },
    { name: 'twitter:url', content: url },
  ];

  if (siteTwitterHandle) {
    twitterMeta.push({ name: 'twitter:site', content: siteTwitterHandle });
  }

  if (twitterImages && twitterImages.length > 0) {
    twitterImages.forEach(i => {
      twitterMeta.push({
        name: 'twitter:image',
        content: i.url,
      });
    });
  }

  if (twitterHandle) {
    // TODO: If we want to connect providers twitter account on ListingPage
    // we needs to get this info among listing data (API support needed)
    twitterMeta.push({ name: 'twitter:creator', content: twitterHandle });
  }

  if (marketplaceRootURL) {
    twitterMeta.push({ name: 'twitter:domain', content: marketplaceRootURL });
  }

  return twitterMeta;
};

/**
 * These will be used with Helmet <meta {...metaTagProps} />
 * Creates data for Open Graph and Twitter meta tags.
 */
export const metaTagProps = (tagData, config) => {
  const {
    marketplaceRootURL,
    facebookAppId,
    marketplaceName,
    siteTwitterHandle,
    googleSearchConsole,
  } = config;

  const author = tagData.author || marketplaceName;
  const googleSiteVerification = googleSearchConsole?.googleSiteVerification;
  const googleSiteVerificationMaybe = googleSiteVerification
    ? [{ name: 'google-site-verification', content: googleSiteVerification }]
    : [];
  const defaultMeta = [
    { name: 'description', content: tagData.description },
    { name: 'author', content: author },
    ...googleSiteVerificationMaybe,
  ];

  const openGraphMeta = openGraphMetaProps({
    ...tagData,
    facebookAppId,
    marketplaceName,
  });

  const twitterMeta = twitterMetaProps({
    ...tagData,
    marketplaceRootURL,
    siteTwitterHandle,
  });

  return [...defaultMeta, ...openGraphMeta, ...twitterMeta];
};
